import {inject} from '@angular/core';
import {CanActivateFn, Router} from '@angular/router';
import {of} from 'rxjs';
import {AuthService} from '../services/auth.service';
import {MENU_ROLE_SETS, normalizeUserRoles} from '../../app.constants';
import {UserRole} from '../../../models/generated-models';

/**
 * Route-level role gate mirroring the sidebar menu matrix
 * (MENU_ROLE_SETS in app.constants.ts). The route reads `data.roles`:
 * allowed roles for that page. Empty/missing list = allow all logged-in
 * users. Denied users land on `/forbidden`.
 *
 * Roles come from AuthService.effectiveRoles() (token roles + profile
 * authorities) so delegated act-as roles (FARMER_AGENT/MIDDLEMAN_AGENT)
 * are honored, same source the `*hasAnyRole` directive uses for the menu.
 */
export const menuRoleGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const authService = inject(AuthService);

  const allowed = route.data['roles'] as UserRole[] | undefined;
  if (!allowed?.length) {
    return of(true);
  }

  const url = state.url.toString();
  // These profile sub-paths keep their own dedicated guards (onboarding /
  // completion flow) and are reached from the avatar dropdown — they stay
  // reachable for roles outside the /profile menu (staff, plain USER).
  const profileSubPathExceptions =
    /^\/profile\/(onboarding|role-assign|role-invitations(\/.*)?|personal\/edit)$/;
  if (profileSubPathExceptions.test(url)) {
    return of(true);
  }

  const userRoles = normalizeUserRoles(authService.effectiveRoles());
  if (allowed.some(role => userRoles.includes(role))) {
    return of(true);
  }

  // AGENT_OPERATOR attempting to access dashboard is redirected to /profile instead of /forbidden
  if (userRoles.includes('AGENT_OPERATOR') && allowed === MENU_ROLE_SETS.dashboard) {
    console.warn(`[menuRoleGuard] Blocked access to "${url}". Redirecting AGENT_OPERATOR to /profile`, {
      url,
      allowedRoles: allowed,
      userRoles,
    });
    return of(
      router.createUrlTree(['/profile'], {queryParams: route.queryParams}),
    );
  }

  console.warn(`[menuRoleGuard] Blocked access to "${url}". Redirecting to /forbidden`, {
    url,
    allowedRoles: allowed,
    userRoles,
  });
  return of(
    router.createUrlTree(['/forbidden'], {queryParams: route.queryParams}),
  );
};
